// ═══════════════════════════════════════════════════════════════
// Handoff — Analytics
// Metrics, status breakdowns, completion rates
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Stack, Group, Badge, Progress,
  Button, Divider, Select,
} from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import AIBriefing from '../../components/handoff/AIBriefing';
import { primary, semantic } from '../../lib/colors';

export default function HandoffAnalytics() {
  const { notes, actionItems, positions, loadNotes, loadActionItems, loadPositions } = useHandoffStore();
  const { incidents, activeIncident, loadIncidents } = useAuthStore();
  const [showAI, setShowAI] = useState(false);
  const [incidentFilter, setIncidentFilter] = useState<string>(
    activeIncident ? String(activeIncident.id) : 'all'
  );

  useEffect(() => { loadNotes(); loadActionItems(); loadIncidents(); loadPositions(); }, []);

  const selectedIncident = incidentFilter !== 'all'
    ? incidents.find(i => String(i.id) === incidentFilter) ?? null
    : activeIncident ?? (incidents.length > 0 ? incidents.find(i => i.status === 'active') ?? null : null);

  const totalEvents = incidents.length;
  const activeEvents = incidents.filter(e => e.status === 'active').length;
  const closedEvents = incidents.filter(e => e.status === 'closed').length;

  const totalNotes = notes.length;
  const drafts = notes.filter(n => n.status === 'draft').length;
  const submitted = notes.filter(n => n.status === 'submitted').length;
  const acknowledged = notes.filter(n => n.status === 'acknowledged').length;
  const ackRate = totalNotes > 0 ? Math.round(((acknowledged) / Math.max(submitted + acknowledged, 1)) * 100) : 0;

  const totalActions = actionItems.length;
  const pending = actionItems.filter(a => a.status === 'pending').length;
  const completed = actionItems.filter(a => a.status === 'completed').length;
  const overdue = actionItems.filter(a => a.status === 'pending' && a.deadline && new Date(a.deadline) < new Date()).length;
  const completionRate = totalActions > 0 ? Math.round((completed / totalActions) * 100) : 0;

  const dayNotes = notes.filter(n => n.fromShift === 'day').length;
  const nightNotes = notes.filter(n => n.fromShift === 'night').length;

  // Top contributors
  const contributors: Record<string, number> = {};
  notes.filter(n => n.senderName).forEach(n => {
    contributors[n.senderName] = (contributors[n.senderName] || 0) + 1;
  });
  const topContrib = Object.entries(contributors).sort((a, b) => b[1] - a[1]).slice(0, 10);

  // Per-event breakdown
  const eventBreakdown = incidents.map(e => ({
    event: e,
    notes: notes.filter(n => String(n.eventId) === String(e.id)).length,
    actions: actionItems.filter(a => String(a.eventId) === String(e.id)).length,
    pendingActions: actionItems.filter(a => String(a.eventId) === String(e.id) && a.status === 'pending').length,
  })).filter(e => e.notes > 0 || e.actions > 0).sort((a, b) => b.notes - a.notes);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Handoff Analytics</Title>
        <Select
          data={[
            { value: 'all', label: 'All Incidents' },
            ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
          ]}
          value={incidentFilter}
          onChange={v => setIncidentFilter(v ?? 'all')}
          size="xs"
          w={220}
        />
      </Group>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700}>{totalEvents}</Text>
          <Text size="xs" c="dimmed">Total Events</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700}>{totalNotes}</Text>
          <Text size="xs" c="dimmed">Total Handoffs</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700} c={semantic.success}>{ackRate}%</Text>
          <Text size="xs" c="dimmed">Ack Rate</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700} c={semantic.info}>{completionRate}%</Text>
          <Text size="xs" c="dimmed">Action Completion</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
        {/* Event Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Event Status</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Active</Text><Text size="xs" fw={500}>{activeEvents}</Text></Group>
              <Progress value={totalEvents > 0 ? (activeEvents / totalEvents) * 100 : 0} color="green" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Closed</Text><Text size="xs" fw={500}>{closedEvents}</Text></Group>
              <Progress value={totalEvents > 0 ? (closedEvents / totalEvents) * 100 : 0} color="gray" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>

        {/* Handoff Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Handoff Status</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Draft</Text><Text size="xs" fw={500}>{drafts}</Text></Group>
              <Progress value={totalNotes > 0 ? (drafts / totalNotes) * 100 : 0} color="gray" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Submitted</Text><Text size="xs" fw={500}>{submitted}</Text></Group>
              <Progress value={totalNotes > 0 ? (submitted / totalNotes) * 100 : 0} color="yellow" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Acknowledged</Text><Text size="xs" fw={500}>{acknowledged}</Text></Group>
              <Progress value={totalNotes > 0 ? (acknowledged / totalNotes) * 100 : 0} color="green" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>

        {/* Action Status */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Action Items</Text>
          <Stack gap="xs">
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Pending</Text><Text size="xs" fw={500}>{pending}</Text></Group>
              <Progress value={totalActions > 0 ? (pending / totalActions) * 100 : 0} color="cyan" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs" c={semantic.error}>Overdue</Text><Text size="xs" fw={500} c={semantic.error}>{overdue}</Text></Group>
              <Progress value={totalActions > 0 ? (overdue / totalActions) * 100 : 0} color="red" size="sm" radius="xl" />
            </div>
            <div>
              <Group justify="space-between" mb={2}><Text size="xs">Completed</Text><Text size="xs" fw={500}>{completed}</Text></Group>
              <Progress value={totalActions > 0 ? (completed / totalActions) * 100 : 0} color="green" size="sm" radius="xl" />
            </div>
          </Stack>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Shift Split */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Shift Distribution</Text>
          <SimpleGrid cols={2}>
            <Paper p="sm" radius="sm" bg="#FDF8F1" ta="center">
              <Text fz={24} fw={700} c={semantic.warning}>{dayNotes}</Text>
              <Text size="xs" c={semantic.warning}>Day Shift</Text>
            </Paper>
            <Paper p="sm" radius="sm" bg="#F1F0FC" ta="center">
              <Text fz={24} fw={700} c={primary[700]}>{nightNotes}</Text>
              <Text size="xs" c={primary[700]}>Night Shift</Text>
            </Paper>
          </SimpleGrid>
        </Paper>

        {/* Top Contributors */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Top Contributors</Text>
          {topContrib.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center">No data yet</Text>
          ) : (
            <Stack gap="xs">
              {topContrib.map(([name, count], i) => (
                <Group key={name} justify="space-between">
                  <Text size="sm">{i + 1}. {name}</Text>
                  <Badge size="xs" variant="light" color="blurple">{count} handoffs</Badge>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* AI Operational Briefing */}
      {selectedIncident && (
        <Paper p="md" radius="sm" withBorder mt="md" mb="md">
          <Group justify="space-between" mb={showAI ? 'md' : 0}>
            <Group gap="xs">
              <IconSparkles size={18} color={primary[700]} />
              <Text fw={600} size="sm">AI Operational Briefing</Text>
            </Group>
            <Button
              size="xs"
              variant={showAI ? 'light' : 'filled'}
              leftSection={<IconSparkles size={14} />}
              onClick={() => setShowAI(!showAI)}
            >
              {showAI ? 'Hide Briefing' : 'Generate AI Briefing'}
            </Button>
          </Group>
          {!showAI && (
            <Text size="sm" c="dimmed" mt="xs">
              Generate a comprehensive AI-powered operational briefing with executive summary, hot issues, strengths & gaps, and priority actions.
            </Text>
          )}
          {showAI && (
            <>
              <Divider mb="md" />
              <AIBriefing
                incident={selectedIncident}
                notes={notes}
                actionItems={actionItems}
                positions={positions}
              />
            </>
          )}
        </Paper>
      )}

      {/* Per-Event Table */}
      {eventBreakdown.length > 0 && (
        <Paper p="md" radius="sm" withBorder mt="md">
          <Text fw={600} size="sm" mb="sm">By Event</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>Event</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>Handoffs</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {eventBreakdown.map(row => (
                  <tr key={row.event.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{row.event.name}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <Badge size="xs" color={row.event.status === 'active' ? 'green' : 'gray'} tt="capitalize">{row.event.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{row.notes}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{row.actions}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: row.pendingActions > 0 ? 'var(--color-warning)' : undefined }}>
                      {row.pendingActions}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
